import React, { memo, FC } from 'react'

interface Props {
  closeModal(): void
}

const TermsModal: FC<Props> = ({ closeModal }) => {
  return (
    <>
      <div className="modal fade show d-block">
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl">
          <div className="modal-content">
            <div className="modal-header align-items-center">
              <button aria-hidden="true" className="close pull-right" type="button" onClick={closeModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <h2>Sociavore Purchase Policy, Privacy Policy and Terms of Use</h2>
              <h3 className="mt-7 mb-2">Purchase Policy</h3>
              <h4 className="mt-6 mb-3">Welcome to Sociavore!</h4>
              <p>
                Thanks for using our solutions to place your order. The following is designed to ensure your
                satisfaction and understanding of the purchasing process on websites and applications which use
                Sociavore products and services, hereinafter referred to as (&quot;Solutions&quot;).
              </p>
              <p>
                If you have any questions, please contact us by email at hello@sociavore.co. Your purchases on websites
                using the Solutions are subject to these Purchase Policies. Please also review our Terms of Use and our
                Privacy Policy which govern your use of the Solutions on the website of our partner merchants.
              </p>
              <h4 className="mt-6 mb-3">Who do you buy from?</h4>
              <p>
                Sociavore acts as an intermediary for businesses who use our ordering Solutions, hereinafter referred to
                as (&quot;Merchants&quot;). When you purchase an item or food from a Merchant located in the United
                States, Canada or Europe, Sociavore manages the online order directly on the Merchant&apos;s website.
                Your order passes through the Sociavore online ordering solution that collects payment on behalf of the
                Merchant via a secure payment platform. Each Merchant has its own merchant account and provides returns
                and refunds in accordance with their own policies, terms and conditions of sale.
              </p>
              <h4 className="mt-6 mb-3">Currency</h4>
              <p>
                All prices for items offered by Merchants located in the United States are displayed in US dollars. All
                prices for items offered by Merchants located in Canada are displayed in Canadian dollars. All prices of
                items offered by Merchants located in the European territory are displayed in Euro with the exception of
                items offered by Merchants located in the United Kingdom which are displayed in Pound.
              </p>
              <h4 className="mt-6 mb-3">Payment Methods</h4>
              <p>The accepted payment methods is based on the Merchant&apos;s own merchant account.</p>
              <h4 className="mt-6 mb-3">Prices and Availability</h4>
              <p>
                Merchants that use our Solutions to sell items and food are responsible for setting prices, which means
                that we do not set the price of items and food, nor do we determine or assume in any way the delivery
                method such as take out, home delivery, table and / or in-room dining, or self-orders on a kiosk. In
                addition, we do not manage the inventory of the Merchants. Therefore, we do not guarantee in any way
                that the items and food you order on the Merchant’s website via the Sociavore online ordering tool are
                available when you make the purchase.
              </p>
              <h4 className="mt-6 mb-3">Confirmation of orders</h4>
              <p>
                If you do not receive an order number (in the form of a confirmation page and / or email and / or text
                message) after submitting the payment information, or if you receive a confirmation message error or if
                you suffer a break in service after having submitted the payment information, it is your responsibility
                to confirm whether your order has been placed or not via the Merchant’s customer service department.
                Only you may be aware of issues that may have occurred during the purchase process. We will not be
                responsible for any loss (monetary or otherwise) or any delay in the delivery of your order.
              </p>
              <h4 className="mt-6 mb-3">Application fees and order processing fees</h4>
              <p>
                Items and food purchased on the Merchant&apos;s website using the Solutions developed by Sociavore are
                generally subject to an Application fee per order and / or a non-refundable Sociavore order processing
                fee for each order made through our Solutions. In many cases, delivery charges charged by the Merchant
                or a third party will also be due. In all cases, if you do not wish to pay the Application fee per order
                and / or the non-refundable order processing fee for each order, you may place your order by phone
                directly with the Merchant.
              </p>
              <h4 className="mt-6 mb-3">Billing information verification</h4>
              <p>
                All orders are subject to credit card approval and verification of the cardholder&apos;s name, billing
                postcode, and card verification number, also known as the Cardholder Verification Value (CVV). We
                sometimes receive incorrect information about the credit card holder, billing postcode, CVV. This will
                block your transaction and prevent the processing and delivery of your order. The Merchant will not
                receive an error message or notification to the effect that your order has been refused as Sociavore
                will not be able to proceed to your order. An error message notifying you that the process has not been
                completed will be displayed and you will not receive an order number for your order.
              </p>
              <h4 className="mt-6 mb-3">Delivery options</h4>
              <p>
                Different combinations of delivery methods may be offered by a Merchant, depending on factors including
                the place from which you place your order or your geolocation to the Merchant. However, it is important
                to note that Sociavore does not make deliveries and does not assume any delay or delivery conditions.
                Sociavore displays the options offered by the Merchant according to the information that you make
                available through your browser and / or your mobile device to enable you to choose among the options
                offered by the Merchant.
              </p>
              <p>
                Here are some of the options generally offered by Merchants that use Sociavore&apos;s online ordering
                Solutions: Pickup Ordering, Delivery Ordering, Table Ordering, or Room Ordering.
              </p>
              <p>
                For any additional information regarding our Purchasing Policy, please contact us at the following
                address:
              </p>
              <p>
                <strong>Sociavore Inc.</strong>
                <br />
                151 Charles St W #100
                <br />
                Kitchener, ON
                <br />
                N2G 1H6
                <br />
                hello@sociavore.co
              </p>

              <h3 className="mt-7 mb-2">Privacy Policy</h3>
              <p className="mt-4">
                <i>Effective March 1, 2018 (last modified: January 10, 2021)</i>
              </p>
              <h4 className="mt-6 mb-3">Introduction</h4>
              <p>
                As part of our mission of helping restaurants deliver digital hospitality online, Sociavore Inc.
                collects and processes a lot of information. This Privacy Policy is intended to help you better
                understand how we collect, use and store your personal information—whether you are a Merchant or end
                user that uses Sociavore’s products, applications or services (together, the “Solutions”), a guest that
                visits a Merchant using our technology, a participant in Sociavore’s “Partners” program, or whether
                you’re simply visiting this website.
              </p>
              <p>
                By using any of Sociavore’s Solutions, or by dealing with a Merchant using Sociavore’s Solutions, you
                are agreeing to the terms of this Privacy Policy and, as applicable, the Terms of Service and Purchase
                Policy. For the purposes of this Privacy Policy, “this Website” means all the web pages related to the
                sociavore.co site; scvr.co site; and the Merchant’s website when provided by our Solutions, excluding
                any links to third party websites.
              </p>
              <p>
                We may update this Privacy Policy from time to time in order to reflect, for example, changes to our
                privacy practices or for other operational, legal, or regulatory reasons. If we make material changes to
                this Privacy Policy, we will give you notice of such changes by posting the revised policy on this
                Website, and where appropriate, by other means. By continuing to use this Website or the Support Service
                after these changes are posted, you agree to the revised policy. At Sociavore Inc. we are committed to
                the protection of your privacy. In this notice we will explain why and how we in the capacity as a data
                processor for our Merchants, collect, use, disclose, retain and protect your personal data. We
                understand your rights and will explain how you can exercise those rights.
              </p>
              <p>
                When we refer to ‘we’, ’us’ and ‘our’ we mean Sociavore Inc. a company registered in Kitchener, Ontario,
                Canada in cooperation with the Merchants we serve, who are the data collectors while we act as the data
                processor. Our registered office is located at 151 Charles St W #100, Kitchener, ON, N2G 1H6.
              </p>
              <p>
                If you have any questions about how we process your personal data, or have questions about your data
                subject rights, please email us at hello+dpo@sociavore.co.
              </p>
              <h4 className="mt-6 mb-3">Information from Merchants</h4>
              <p>
                Privacy matters! If you are a Merchant, you agree to obtain consent from your customers for the use and
                access of their Personal Information by Sociavore and other third parties. In addition, if you are
                collecting any sensitive Personal Information from your customers (including information relating to
                medical or health conditions, racial or ethnic origin, political opinions, religious or philosophical
                beliefs, trade union membership or sexuality), you agree to obtain affirmative, express consent from
                your customers for the use and access of sensitive Personal Information by Sociavore and other third
                parties.
              </p>
              <p>
                <strong>What information do we collect from Merchants and why?</strong>
              </p>
              <p>
                We collect your name, company name, address, email address, phone number(s) and credit card details.
                <br />
              </p>
              <p>
                We need this information to provide you with our Solutions; for example, to confirm your identity,
                contact you, and invoice you.
                <br />
              </p>
              <p>
                We collect data about the Sociavore-hosted web pages that you visit. We also collect data about how and
                when you access your account, including information about the device and browser you use, your network
                connection and your IP address.
              </p>
              <p>We need this information to give you access to and improve our Solutions.</p>
              <p>
                We collect Personal Information about your customers that you share with us or that customers provide
                while providing feedback, booking reservations, shopping or during checkout.
              </p>
              <p>
                We use this information to provide you with our Solutions and so that you can process reservations and
                orders and better serve your customers.
              </p>
              <p>
                We will also use Personal Information in other cases where you have given us your express permission.
              </p>
              <p>
                <strong> When do we collect this information?</strong>
              </p>
              <p>
                We collect this Personal Information when you visit Sociavore-hosted webpages, use Solutions offered on
                our websites or engage with us either by email, web form, instant message, phone, SMS text messaging, or
                post content on or through our websites (including blogs and via any Messaging Feature). We also collect
                any additional information that you might provide to us.
              </p>
              <h4 className="mt-6 mb-3">Information from our Merchants’ customers</h4>
              <p>
                <strong>What information do we collect and why?</strong>
              </p>
              <p>We collect contact and payment information (direct collection, core service):</p>
              <p>
                Private consumer: first name, last name, address and delivery address if not the same, email, phone
                number, credit card information
              </p>
              <p>
                Corporate consumer: company name, phone number, address and delivery address if not the same, credit
                card information
              </p>
              <p>Interaction with our staff and postings (direct collection, voluntary):</p>
              <p>
                Correspondence and communications with us, information you might provide through email, chat, or SMS
                messaging.
              </p>
              <p>
                Posts on our social media channels, comments you leave on our websites or an Merchants’ website, forms
                your complete on our websites or an Merchants’ website
              </p>
              <p>Loyalty Program (direct and indirect collection, optional):</p>
              <p>Email and password</p>
              <p>Gift or loyalty card used</p>
              <p>First and last name</p>
              <p>Phone number</p>
              <p>Birth date and gender</p>
              <p>Address and delivery address if not the same</p>
              <p>Preferred language for correspondence</p>
              <p>Amount spent and items bought</p>
              <p>Demographic information (indirect collection, optional):</p>
              <p>Products visited</p>
              <p>Products ordered</p>
              <p>Website information (indirect collection, optional):</p>
              <p>
                Merchant websites visited that use one of our solutions, the website you came from and the website you
                go to from there
              </p>
              <p>the browser you use</p>
              <p>GPS location of your mobile or the unique identifier of your device</p>
              <p>Type of mobile device you are using</p>
              <p>The operating system version of your mobile device</p>
              <p>Frequency with which you use the application</p>
              <p>Transactions that you make with the application</p>
              <p>
                <strong>When do we collect this information?</strong>
              </p>
              <p>
                Information is collected when a Merchant’s customer uses or accesses our Solutions, such as when a
                customer visits a Merchant’s website, books a reservation, provides feedback, places an order or signs
                up for an account on a Merchant’s website.
              </p>
              <h4 className="mt-6 mb-3">Information from Partners</h4>
              <p>
                Partners are individuals or businesses that have agreed to the terms of the Sociavore Partner Program to
                work with Sociavore to promote the Solutions by (a) referring clients to Sociavore; or (b) developing
                Sociavore store themes for Merchant use; or (c) using the Sociavore Application Interface (API) for
                Merchant use.
              </p>
              <p>
                <strong>What information do we collect from Partners and why?</strong>
              </p>
              <p>
                We collect your name, company name, website, twitter or other social media handles, phone number(s),
                address, business type, email address, and GST/HST number.
              </p>
              <p>We use this information to work with you, confirm your identity, contact you, and pay you.</p>
              <p>
                We collect data about the Sociavore-hosted webpages that you visit and how and when you access your
                account, including information about the device and browser you use, your network connection and your IP
                address.
              </p>
              <p>We use this information to give you access to and improve our Solutions.</p>
              <p>
                We collect Personal Information about your customers that you share with us or that they provide to us
                directly.
              </p>
              <p>We use this information to work with you and to provide our Solutions to your customers.</p>
              <p>We will also use Personal Information in other cases where you have given us express permission.</p>
              <p>
                <strong>When do we collect this information?</strong>
              </p>
              <p>
                We collect this information when you sign up for a Partner Account, when you sign up one of your
                customers for our Solutions, or when your customers sign up themselves. We also collect any additional
                information that you might provide to us.
              </p>
              <h4 className="mt-6 mb-3">
                What information do we collect from Sociavore website visitors and support users and why?
              </h4>
              <p>
                From Sociavore website visitors, we collect information about the device and browser you use, your
                network connection and your IP address. We also collect Personal Information submitted by you via any
                messaging or chat feature available from any of our websites (“Messaging Feature”).
              </p>
              <p>
                We may also receive Personal Information when you purchase or make other requests to Sociavore via any
                of our websites.
              </p>
              <p>From telephone support users, we collect your phone number and call audio.</p>
              <p>
                From chat support users, we collect your name, email address, information about the device and browser
                you use, your network connection, your IP address and chat transcript.
              </p>
              <p>From blog users, we collect your name, email address and website URL.</p>
              <p>
                We use this information to provide and enhance our Solutions (including servicing your account, if
                applicable), and answer any questions you may have.
              </p>
              <p>
                <strong>When do we collect this information?</strong>
              </p>
              <p>
                We collect this information when you visit Sociavore-hosted webpages, use Solutions offered on our
                websites or engage with us either by email, web form, instant message, phone, SMS text messaging, or
                post content on or through our websites (including blogs and via any Messaging Feature). We also collect
                any additional information that you might provide to us.
              </p>
              <h4 className="mt-6 mb-3">Information from cookies and similar tracking technology</h4>
              <p>
                What is a cookie? A cookie is a small amount of data, which may include a unique identifier. Cookies are
                sent to your browser from a website and stored on your device. We assign a different cookie to each
                device that accesses our website.
              </p>
              <p>
                <strong>Why does Sociavore use cookies and similar tracking technology?</strong>
              </p>
              <p>
                We use cookies to recognize your device and provide you with a personalized experience. We also use
                cookies to serve targeted ads from Google, Facebook, Bing, and other third-party vendors.
              </p>
              <p>
                Functionality cookies – required to allow you to choose products and check out. As such we will not ask
                for expressed consent.
              </p>
              <p>
                Our third-party advertisers use cookies to track your prior visits to our websites and elsewhere on the
                Internet in order to serve you targeted ads.
              </p>
              <p>
                Opting out: You can opt out of targeted ads served via specific third party vendors by visiting the
                Digital Advertising Alliance’s Opt-Out page.
              </p>
              <p>
                We may also use web beacons, tracking technology and other automated tracking methods on our websites,
                in communications with you, and in our products and Solutions, to measure performance and engagement.
              </p>
              <p>
                Please note that because there is no consistent industry understanding of how to respond to “Do Not
                Track” signals, we do not alter our data collection and usage practices when we detect such a signal
                from your browser.
              </p>
              <p>
                The purpose of a cookie is to identify you electronically, store your preferences for viewing our web
                pages, allow “remarketing” relevant to your interests based on your visits to our site, and show you
                relevant ads on our website and across the Internet.
              </p>
              <p>
                Cookies are stored on your device to assist us in providing and improving our Solutions. For example, we
                use cookies to keep you logged into your account and we use data collection devices such as Google
                Analytics to help analyze our web page flow, measure promotional effectiveness and to provide you with
                information relevant to your interests.
              </p>
              <p>
                Through first-party and third-party cookies, third parties may collect information about you while you
                are visiting Sociavore-hosted web pages and other websites. They may use this data to show you
                advertisements across the Internet based on your prior visits to these sites. We do not collect this
                information or control the content of the advertisements that you will see.
              </p>
              <h4 className="mt-6 mb-3">How and when do we process your personal data?</h4>
              <p>We collect and process your personal data when:</p>
              <p>When you visit our website</p>
              <p>When you visit a Merchant’s website</p>
              <p>When you visit our social media websites</p>
              <p>When you browse Merchants’ products</p>
              <p>When you order Merchants’ products</p>
              <p>When you submit Merchants’ forms</p>
              <p>When you contact us through email, SMS, chat, phone</p>
              <p>When you attend events hosted by us or industry events that we attend</p>
              <p>
                <strong>What is the legal basis for the personal data collection?</strong>
              </p>
              <p>
                Legitimate interest: we use your personal contact information to enable you to order Merchants’
                products, confirm the successful order and have the products delivered if delivery applies. We assume
                that you understand that we need your contact and payment information to provide this service. We
                therefore will not ask for explicit consent at the time when you place an order and/or when you open an
                account.
              </p>
              <p>
                To comply with legal obligation: in certain circumstances we will have to disclose your personal data in
                response to an inquiry from a regulatory authority, the police or other government bodies.
              </p>
              <p>
                We use information to respond to your inquiries or questions: For example, we could use your information
                to confirm your order to a Merchant or to register for a contest.
              </p>
              <p>
                We use the information to improve our products and services: We can use your information to personalize
                your experience with us. This could include display content and / or offer you promotions based on your
                preferences.
              </p>
              <p>
                We use information to examine application trends and interests: We may use your information to improve
                our solutions and our products. We can combine the information we get from you with the information
                about you that we receive from third parties.
              </p>
              <p>
                We use information for security purposes: We can use the information to protect our company, our
                customers, our partners or our websites.
              </p>
              <p>
                We use information for marketing purposes: For example, we could send information about promotions or
                special offers from Merchants that use our Solutions. We could also share about new features or new
                products. It may be our own offers or our products, or offers or products from third parties that we
                think you could find interesting. Or, for example, if you buy products through the website of our
                partner Merchant, we will subscribe you to our newsletter depending on your preferences. We can use
                Direct Push and your geographical information on our Solutions to send you alerts about local
                promotions. To learn more about your choices regarding these communications, please read the section
                about the choices below.
              </p>
              <p>
                We use the information to send you transactional communications: For example, we may send you emails to
                your email account or via different social media platforms. We could also contact you about this policy
                or our Solution.
              </p>
              <p>
                We do not have a legal basis other than consent to collect demographic and technical information for
                marketing purposes. We will therefore ask for your expressed consent before we start the collection.
              </p>
              <p>
                We can use such information to provide you with offers of our products or offers or products from third
                parties that we think you would find interesting.
              </p>
              <h4 className="mt-6 mb-3">When and why do we share Personal Information with third parties?</h4>
              <p>
                Sociavore works with third parties to help provide you with our Solutions and we may share Personal
                Information with them to support these efforts. In certain limited circumstances, we may also be
                required to share information with third parties to conform to legal requirements or to respond to
                lawful requests by public authorities, including to meet national security or law enforcement
                requirements. We may also receive
              </p>
              <p>Personal Information from our partners and third parties.</p>
              <p>We may share your personal information with:</p>
              <p>Third parties that help us deliver orders or Merchants that prepare your order</p>
              <p>Third parties that provide a Merchant or operate a Merchant for which we offer the online order</p>
              <p>Third Parties that process your payment</p>
              <p>Integrated POS system that might show your name on the receipt</p>
              <p>Integrated printers that show your name, address(es), phone on the receipt</p>
              <p>Our employees and contractors for specific business reasons</p>
              <p>Our subsidiaries and sister companies</p>
              <p>
                Canadian research centers such as University of Waterloo or researchers and companies whom we develop
                our recommendation models and analysis of your preferences (sharing anonymized data only)
              </p>
              <p>Providers of loyalty programs to which you subscribe such as Silverware</p>
              <p>Providers of order aggregator programs to which you subscribe such as Cuboh</p>
              <p>
                Personal Information may be shared with third parties to prevent, investigate, or take action regarding
                illegal activities, suspected fraud, situations involving potential threats to the physical safety of
                any person, violations of our Terms of Service or any other agreement related to the Solutions, or as
                otherwise required by law.
              </p>
              <p>
                Personal Information may be shared with third party vendors to help us conduct marketing and/or
                advertising campaigns.
              </p>
              <p>
                Personal Information may also be shared with a company that acquires our business, whether through
                merger, acquisition, bankruptcy, dissolution, reorganization, or other similar transaction or
                proceeding.
              </p>
              <p>
                Sociavore is responsible for all onward transfers of Personal Information to third parties in accordance
                with the EU-U.S. Privacy Shield Principles, the U.S.-Swiss Safe Harbor Framework, and Canada’s Personal
                Information Protection and Electronic Documents Act (PIPEDA).
              </p>
              <p>
                Sociavore will always ask for your consent before sharing your Personal Information with third parties
                for purposes other than those described above.
              </p>
              <p>
                Sociavore remains responsible for Personal Information that is transferred to a third party for
                processing or to support our efforts in providing you with our Solutions. Any Personal Information
                transferred to a third party for data processing is processed in accordance with our instructions, and
                is subject, by law, to a comparable level of protection as that provided by Sociavore.
              </p>
              <p>
                A “comparable level of protection” means a level of protection generally equivalent to that provided by
                Sociavore.
              </p>
              <h4 className="mt-6 mb-3">Transborder Data Flows</h4>
              <p>
                While Sociavore Inc. is a Canadian company, we provide Solutions to customers and our technology
                processes data from users around the world. Accordingly, Sociavore may transmit your personal
                information outside of the country, state, or province in which you are located.
              </p>
              <p>
                Data collected in Canada transferred to locations outside of Canada and Restricted Transfers (personal
                data transfers outside of the EEA)
              </p>
              <p>
                Your personal data is held in Google Cloud Platform (GCP) in Canada and the United States of America.
                GCP was able to evidence that sufficient safeguards are in place to protect your personal data.
              </p>
              <p>
                We will not ask for your expressed consent and we cannot offer you alternatives as the IT infrastructure
                in the Google Cloud Platform is integral to the delivery of our services.
              </p>
              <h4 className="mt-6 mb-3">
                What do we do with your Personal Information when you terminate your relationship with us?
              </h4>
              <p>
                We will continue to store archived copies of your Personal Information for legitimate business purposes
                and to comply with the law.
              </p>
              <p>
                We will continue to store anonymous or anonymized information, such as website visits, without
                identifiers, in order to improve our Solutions.
              </p>
              <h4 className="mt-6 mb-3">What we don’t do with your Personal Information</h4>
              <p>
                We do not and will never share, disclose, sell, rent, or otherwise provide Personal Information to other
                companies (other than to specific Sociavore Merchants you may be interacting with) for the marketing of
                their own products or Solutions.
              </p>
              <p>
                If you are a Merchant using Sociavore’s Solutions, we do not use the Personal Information we collect
                from you or your customers to independently contact or market to your customers. However, Sociavore may
                contact or market to your customers if we obtain their information from another source, such as from the
                customers themselves.
              </p>
              <h4 className="mt-6 mb-3">We obtain Personal Information about you from third parties.</h4>
              <p>
                For example, if you use an integrated Google and social media feature (Facebook, Twitter, LinkedIn,
                etc.) on our websites or mobile apps. The third-party Google and social media site will give us some
                information about you. This could include your name and your email address.
              </p>
              <h4 className="mt-6 mb-3">How do we keep your Personal Information secure?</h4>
              <p>
                We follow industry standards on information security management to safeguard sensitive information, such
                as financial information, intellectual property, employee details and any other Personal Information
                entrusted to us. Our information security systems apply to people, processes and information technology
                systems on a risk management basis.
              </p>
              <p>
                No method of transmission over the Internet, or method of electronic storage, is 100% secure. Therefore,
                we cannot guarantee the absolute security of your Personal Information.
              </p>
              <h4 className="mt-6 mb-3">Control and access to your Personal Information</h4>
              <p>
                You retain all rights to your Personal Information and can access it anytime. In addition, Sociavore
                takes reasonable steps to allow you to correct, amend, delete, or limit the use of your Personal
                Information. You can update many types of Personal Information, such as payment or contact information,
                directly within your account settings. If you are unable to change your Personal Information within your
                account settings, please contact us to make the required changes. It’s important to remember that if you
                delete or limit the use of your Personal Information, the Solutions may not function properly.
              </p>
              <p>
                <strong>Your Rights</strong>
              </p>
              <p>Right to access: you can access your account any time through the website or the mobile application</p>
              <p>Right to rectify: you can make changes to your account information at any time</p>
              <p>
                Right to be forgotten: You can delete your account, which will automatically delete all personal data
                associated with the account. We will instruct all third parties that we have shared your data with to
                delete your data.
              </p>
              <p>
                Right to restrict processing: you can change your preferences at any time through your account settings.
              </p>
              <p>
                Right to data portability: should we receive a subject access request from you we will make the data
                that we have about you available to you.
              </p>
              <p>
                Right to object: you have full control over your account. Deleting your account will delete all
                associated data and consequently we will stop processing your personal data with us and all third
                parties we have shared your data with.
              </p>
              <p>If you contact us to request access to your information, we will reply within 30 days.</p>
              <h4 className="mt-6 mb-3">You may refuse to receive our marketing emails.</h4>
              <p>
                To stop receiving these promotional emails, you can unsubscribe by following the instructions contained
                in the promotional message we send you. Your device settings should provide guidance on how to disable
                the Direct Push notice. A delay of ten days may be required to process your request. We will always send
                you transactional messages. For example, we can always contact you about your orders.
              </p>
              <h4 className="mt-6 mb-3">
                We may establish links to third party websites or services that we do not control.
              </h4>
              <p>
                If you click on one of these links, you will be directed to websites that we do not control. This policy
                does not apply to the privacy practices of these websites. Please read the privacy policy of other
                websites carefully. We are not responsible for these third party websites.
              </p>
              <p>
                If you have any questions about your Personal Information or this policy, or if you would like to make a
                complaint about how Sociavore processes your personal data, please contact Sociavore by email at
                hello+dpo@sociavore.co.
              </p>

              <h3 className="mt-7 mb-2">Terms of Use for End-Users (Merchant clients)</h3>
              <p>
                This document describes the Terms of Use (hereinafter referred to as the &quot;Terms&quot;) that govern
                your use of the ordering solutions offered by Sociavore including our web applications, mobile websites
                and self-order kiosks (collectively, the &quot;Solutions&quot;). Our Privacy Policy, our Purchasing
                Policy, and any other policies, rules or guidelines that may be applicable to any of the Solutions or
                features of the Solutions are also available on our website which you can visit here. By visiting or
                using our Solutions, you expressly agree to abide by these Terms as updated from time to time.
              </p>
              <p>
                We may make changes to these Terms at any time. The changes we will make will be effective immediately
                when we post a revised version of these Terms in our Solutions. The date of the &quot;last update&quot;
                above tells you when these Terms have been last revised. By continuing to use these Solutions after this
                date, you agree to the changes.
              </p>
              <p>
                The Solutions are not intended for children under 13, and no one under the age of 13 can use the
                Solutions. We strongly encourage all parents and guardians to monitor the use of the Internet by their
                children. If you use the Solutions, you claim that you are at least 13 years old.
              </p>
              <h4 className="mt-6 mb-3">Registration and/or account creation</h4>
              <p>
                You can browse the Solutions and make transactions without creating an account. You will be required to
                register and / or create an account to use certain features, such as the ability to reorder a product or
                food from a previous order. The username of your account is your email address. It must not include the
                name of another person with the intent to impersonate that person, or be offensive, vulgar or obscene.
                Your username and password are personal. You will be responsible for the confidentiality and use of your
                username and password, and for all activities (including purchases) that are conducted through your
                online or offline account. You can not transfer or sell access to your account or use someone
                else&apos;s email address. We will not be liable for any damages related to the disclosure of your
                password or the use by another person of your username or password. You can not use another user account
                without the user&apos;s permission. You must notify us immediately in writing if you discover an
                unauthorized use of your account or a security breach of any other account. We may ask you to change
                your username and / or password if we believe your account is no longer secure or if we receive a
                complaint that your username violates the rights of another person. You will not have any ownership of
                your account or your username. We reserve the right to refuse registration, cancel an account or deny
                access to the Solutions for any reason.
              </p>
              <h4 className="mt-6 mb-3">Code of conduct</h4>
              <p>
                You agree to abide by all applicable laws, rules and regulations, and agree that you are not authorized
                to:
              </p>
              <p>Restrict access or prevent others from using the Solutions;</p>
              <ul>
                <li>Use the Solutions for illegal purposes;</li>
                <li>
                  Express or suggest that any of your statements are approved by us without our prior written consent;
                </li>
                <li>
                  Impersonate any person or entity, real or fictional, including any employee or representative of our
                  company;
                </li>
                <li>
                  Present (a) any content or information that is unlawful, fraudulent, defamatory, or otherwise
                  objectionable, or infringe on the intellectual property of Solutions or a third party; (b) any
                  confidential information about companies without authorization; or (c) any advertising, solicitations,
                  chain letters, pyramid schemes, surveys, contests, investment opportunities or other unsolicited
                  commercial communications;
                </li>
                <li>
                  Submit, or provide links to, publications that contain material that could be considered harmful,
                  obscene, pornographic, sexually explicit, indecent, obscene, violent, abusive, vulgar, insulting,
                  threatening, harassing, hateful or otherwise objectionable, including images or appearances of
                  individuals under the age of 18, as well as material that encourages or otherwise idealizes drug use
                  (including alcohol and cigarettes), considers violence as something acceptable, prestigious or
                  desirable, or contains contact information or other personal information to identify third parties;
                </li>
                <li>
                  Submit, or provide links to, publications that contain material that harasses, victimizes, degrades,
                  or intimidates an individual or group of individuals on the basis of religion, race, ethnicity, sexual
                  orientation, sex, age or disability;
                </li>
                <li>Spread spam or participate in hacking activities;</li>
                <li>Collect information about the users of the Solutions;</li>
                <li>
                  Use a password or a participation code in a presale or other offer on the Solutions if you have not
                  received the password or code or if you do not comply with the terms of the offer.
                </li>
              </ul>
              <h4 className="mt-6 mb-3">Content Ownership and Conditional Granting Licensing</h4>
              <p>
                Solutions and all data, text, drawings and models, pages, screenshots, images, illustrations,
                photographs, audio and video clips, and HTML code, source or software code that is present or visible or
                otherwise detectable on the Solutions (collectively, the &quot;Content&quot;) is our property or that of
                our Establishments and / or licensors. We have a copyright on the Solutions and its Content. We may
                change the Content and functionality of the Solutions at any time.
              </p>
              <p>
                We grant Establishments, with an Application Fee on each order or a monthly payment for the self-order
                kiosks, a limited and conditional, non-exclusive, non-transferable license that can not be licensed for
                sale for the use of Solutions and their Content, as permitted by these Terms for business purposes only.
                In addition, we grant you the right to use our Solutions if, as a precondition, you agree not to:
              </p>
              <ul>
                <li>
                  Submit any software or Content that contains viruses, worms, Trojan, malfunctions, a time-sensitive
                  virus mechanism or any other elements of a destructive nature;
                </li>
                <li>
                  Manipulate identifiers, by forging headers, to conceal the origin of any publication you submit;
                </li>
                <li>
                  Link to any part of the Solutions other than the URL assigned to the homepage of the websites of the
                  Establishments using the Solutions;
                </li>
                <li>Create a mirror image of any part of Solutions and websites using the Solutions;</li>
                <li>
                  Modify, adapt, license, translate, sell, make reverse engineering, decompile or disassemble all or any
                  part of the Solutions or make other attempts to derive the source code or ideas from them or
                  algorithms from any part of the Solutions;
                </li>
                <li>Remove any copyright, trademark or other proprietary rights contained on the Solutions;</li>
                <li>
                  Use a robot, an offline reader, an application or a Solutions search engine or other manual or
                  automatic devices, a tool or a process to recover, index, explore data or in any way reproduce or
                  bypass the structure of Solution navigation or presentation or its contents, including with regard to
                  any captcha test posted on the Solutions. Public search engine operators may use robots to copy the
                  contents of the Solutions for the sole purpose of, and only to the extent necessary to create a
                  publicly available index, but may not explore caches or archives of such content. We may terminate
                  this provision at any time;
                </li>
                <li>
                  Use any automated software or computer system to search, purchase or otherwise obtain products or
                  food, promotional codes, vouchers, gift cards or other items available on the Solutions, including
                  sending information from your computer to another computer on which such software or system is active;
                </li>
                <li>
                  Adopt any measure that imposes or may impose (in our sole discretion) an unreasonable or
                  disproportionate burden on our infrastructure;
                </li>
                <li>
                  Accessing, reloading or refreshing transactional events or Solutions pages, or making other requests
                  to transactional servers more than once in any three-second interval;
                </li>
                <li>
                  Request more than 1,000 pages of Solutions in any 24-hour period, alone or with a group of
                  individuals;
                </li>
                <li>
                  Request more than 50 orders from Solutions during any 24-hour period, alone or with a group of
                  individuals;
                </li>
                <li>
                  Reproduce, modify, display, publicly display, distribute or create derivative works of the Solutions
                  or its contents;
                </li>
                <li>
                  Use the Solutions or its content, or use devices, programs or services to circumvent any technical
                  measure that effectively controls the access or rights of the Solutions and / or the Content in any
                  way, including, but not limited to, manual or automatic devices or processes for any purpose.
                </li>
              </ul>
              <p>
                This right is expressly granted under the terms of your pre-existing agreement to comply with each
                provision described in the Content Property and the Conditional License Granting section. This right can
                only exist to the extent that you strictly adhere to each of the provisions described in this section.
                Any use of the Solutions or Content by you or any person acting on your behalf that does not strictly
                comply with all the provisions of this section goes beyond the scope of this right granted to you in
                this document, and constitutes unauthorized reproduction, posting, or creation of unauthorized
                derivative products of the Solutions and its Content, and infringes on our copyrights and other rights
                derived from the Solutions and its Content. You do not acquire any right of ownership by using the
                Solutions or its Content.
              </p>
              <p>
                The registered and unregistered trademarks, logos and service marks displayed on the Solutions are our
                property or the property of our licensors. You may not use our trademarks, logos, and service marks in
                any way without our prior written permission. You can inquire about obtaining an authorization by
                writing to hello@sociavore.co:
              </p>
              <p>
                <strong>Sociavore (Sociavore Inc.)</strong>
              </p>
              <p>151 Charles St W #100, Kitchener, ON, N2G 1H6, Canada</p>
              <p>Attention: Trademark Department, Legal</p>
              <h4 className="mt-6 mb-3">Make purchases</h4>
              <p>
                Please refer to our Purchasing Policy, which will govern your purchasing activities for products, food
                or other items on the websites using the Solutions. Policies regarding refunds or exchanges are specific
                to each Merchant so we recommend that you consult the site of the Merchant on which you are about to
                place an order and read the terms of use before making a purchase through Solutions. We may impose
                conditions on the use of any coupon, promotional code or gift card. You will be required to pay any fees
                incurred by you or by users of your account and the credit card (or other applicable payment mechanism)
                at the price in effect when such charges are charged, including applicable taxes. You may only use
                credit or debit cards, gift cards or items that belong to you or belong to persons who expressly
                authorize you to use such payment methods. You must not attempt to hide your identity by using multiple
                Internet address protocols or email addresses to perform transactions on the Solutions. You must not
                hold us responsible if you have not respected the laws related to your transactions. We can help law
                enforcement agencies with the information you provide us about your transactions to participate in any
                investigation or suit that concerns you. If we are unable to verify or authenticate the information you
                provide during the entire registration, order, purchase, posting, sale, authentication, delivery,
                payment and the transfer of funds or any other procedures, or if we are no longer able to verify or
                authorize your banking or credit card information, your orders may be canceled. We reserve the right to
                refuse to honor all pending purchases and future purchases made with these credit cards or bank accounts
                and / or through online accounts associated with these credit cards or bank accounts. We may also
                prohibit you from using the Solutions on an Merchant&apos;s website.
              </p>
              <h4 className="mt-6 mb-3">Forums and User Content</h4>
              <p>
                We can organize user reviews, forums, blogs, social media feeds and other forums found on websites that
                use the Solutions (collectively called &quot;Forums&quot;), and you may be able to submit suggestions,
                reviews, concepts, audio and video recordings, photographs, drawings, or other materials on the Forums
                or other areas of the Solutions (hereinafter referred to as &quot;User Content&quot;).
              </p>
              <p>
                By submitting User Content, you certify that you are at least 18 years old, or you are at least 13 years
                old, and you have obtained the express consent of your parent or legal guardian before submitting the
                User Content.
              </p>
              <p>
                You hold all rights in your User Content. If you present User Content on the Solutions, you grant us a
                worldwide royalty-free, non-exclusive, transferable right, and a license to use, reproduce, modify,
                create derivative works, distribute, publicly perform and display, archive and market your User Content,
                in our sole discretion, in all formats and media channels known now or to be discovered thereafter
                without offering any compensation or acknowledgment to anyone. This license does not affect the
                ownership of your User Content, including the right to grant further licenses of your User Content,
                unless it conflicts with these Terms. We are not required to post, display or otherwise use the User
                Content, or to specify the source of your User Content. You will not make or allow any claim against us,
                stating that our use of your User Content violates any of your rights.
              </p>
              <p>
                Statements, opinions and comments posted by Forum participants may be inaccurate, offensive, obscene,
                threatening or unwelcome. We do not approve and we are not responsible for these publications. We will
                not be liable for any loss or damage caused by publication or reliance upon this information obtained
                through publications.
              </p>
              <p>
                You will be responsible for your User Content and the consequences of publishing it. By submitting User
                Content, you can confirm to us that (i) you own, or have the necessary permission to display the User
                Content and License under this section, and (ii) you have the written permission of each person
                identifiable in the User Content to use the person&apos;s name and likeness in the manner provided by
                the Solutions and these Terms or, if the person is a minor, the written authorization of the parent or
                legal guardian of the minor person.
              </p>
              <p>
                We have the right (but not the obligation) to monitor Solutions, Forums and User Content, and to
                disclose any User Content and the circumstances surrounding its presentation, in order to operate the
                Solutions properly or to protect our assets, our Partner merchants and our users, or to comply with
                their legal obligations or governmental requests.
              </p>
              <p>
                If we are informed that your User Content does not comply with these conditions, we may investigate the
                allegation and decide to delete your User Content and cancel your account if necessary.
              </p>
              <h4 className="mt-6 mb-3">Claims for Copyright Infringement on Solutions</h4>
              <p>
                If you believe in good faith that some of the content of the Solutions violates your copyright, you may
                send us a notice requesting that the content be removed. The notice must include: (a) your actual or
                electronic signature, or that of your agent; b) the identification of the copyrighted work on our
                Solutions whose rights have been infringed (or a representative list if several copyright works are
                included in a notice); (c) the identification of content whose rights have been infringed or which would
                be subject to counterfeiting, including information reasonably sufficient to allow us to locate the
                content on the Solutions; (d) your name, address, telephone number and email address (if available); (e)
                a statement in which you have a good faith belief that use of the content as described in the complaint
                is not authorized by you or your agent or the law; and (f) a statement that the information in the
                Notice is accurate and, under penalty of perjury, that you or your agent are authorized to act on behalf
                of the copyright owner. If you believe in good faith that a notice of copyright infringement has been
                improperly filed against you, you may send us a counter-notice.
              </p>
              <p>
                Notices and counter-notices should be sent to the Copyright Officer, Sociavore, 151 Charles St W #100,
                Kitchener, ON, Canada, N2G 1H6. We suggest that you consult your legal advisor before filing a notice or
                counter-notice.
              </p>
              <h4 className="mt-6 mb-3">Links</h4>
              <p>
                The Solutions contain links to other websites that may not belong to or be exploited by us. The fact
                that we can establish links to these websites does not mean that we approve of or endorse these
                websites. We have no control over these websites. We are not responsible for the content of these
                websites, or the privacy practices of these websites. We strongly encourage you to become familiar with
                the terms of use and practices of any website to which links have been established. Your use of other
                websites is at your own risk and is subject to the special terms of these websites. You must take the
                necessary precautions to ensure that, regardless of the links you select or the software you download
                (either from the Solutions or other websites), that they are free of viruses, worms, Trojan, defects,
                time-sensitive viral devices and other destructive elements.
              </p>
              <h4 className="mt-6 mb-3">Parental control</h4>
              <p>
                We cannot prohibit minors from viewing content available through our Solutions, and they must rely on
                their parents and guardians to decide what content is appropriate to display and purchase. There are
                parental control protections (such as computer hardware and software or filtering services) that can
                help you limit access to content that is detrimental to minors. You will find information on Parental
                Controls at http://getnetwise.org. We do not recommend any of the specific products or services listed
                in our Solutions.
              </p>
              <h4 className="mt-6 mb-3">Access from outside Canada, the United States and Europe</h4>
              <p>
                The Solutions are for people in Canada, the United States and Europe. We can not guarantee that the
                content available on or offered through the Solutions is appropriate or available in other locations. We
                may limit the availability of Solutions or any product or service described in the Solutions at any time
                to any person or geographic area. If you choose to access Solutions outside Canada, the United States or
                Europe, you do so at your own risk.
              </p>
              <h4 className="mt-6 mb-3">Rules for lucky draws, contests and games</h4>
              <p>
                In addition to these Terms, lotteries, contests, games or other promotions (collectively referred to as
                &quot;Promotions&quot;) submitted through the websites using the Solutions may have rules that are
                different from these Terms. By participating in a promotion, you must respect these rules. We invite you
                to review the rules before participating in a promotion. Promotion rules will govern any potential
                conflict with these Terms.
              </p>
              <h4 className="mt-6 mb-3">Mobile Messaging</h4>
              <p>
                We offer navigation and mobile messaging services that can include text message alerts, emails,
                contests, and product offerings. You can choose to receive mobile alerts when making a purchase,
                subscribing or participating in a Promotion. If you do so, you will also be subject to the Promotion
                Terms of Use. You may receive up to eight (8) advertising messages per month related to your preferences
                and the purchases you made through the Solutions.
              </p>
              <p>
                Rates for messages and data may apply, depending on your rate plan provided by your wireless carrier. We
                are not responsible for any charges related to text messaging or other wireless communications incurred
                by you or anyone who has access to your wireless device or phone number. You may not receive our alerts
                if your operator does not allow text alert messages. Your carrier may not allow you to use prepaid phone
                plans or other calling plans to receive alerts. We can send you an automated message for each message
                you send us. This service may not be compatible with all wireless operators or devices.
              </p>
              <h4 className="mt-6 mb-3">Violation of these conditions</h4>
              <p>
                We may investigate any breach of these terms, including the unauthorized use of the Solutions. We can
                adopt the legal measures we deem appropriate. You agree that monetary damages may not be sufficient
                relief and that we may resort to injunctive relief or any other remedy for breach of these conditions.
                If we determine that you have violated these Terms or the Law, or for any other reason or for no reason,
                we are entitled to cancel your account, delete all of your User Content and prevent you from accessing
                these Solutions at any time, without notice. If this happens, you can no longer use the Solutions or any
                content belonging to it. You will still be bound by your obligations under these Terms. You agree that
                we will not be liable to you or any third party for the termination of your access to the Solutions or
                your account or any related information, and that it is no longer necessary to make the Solutions
                available to you or your account or any related information. We can also cancel any order of items, and
                ordered goods. We reserve the right to refuse to honor pending and future purchases made from any
                accounts that we believe may be associated with you, or cancel an order associated with any person who,
                in our opinion, acts on your behalf, or exercise any other remedy that the law allows us.
              </p>
              <h4 className="mt-6 mb-3">Disclaimer of Warranties</h4>
              <p>
                WE PROVIDE THE SOLUTIONS AND ITS CONTENT FOR YOU &quot;AS IS&quot; AND &quot;AS AVAILABLE.” WE TRY TO
                KEEP SOLUTIONS IN PLACE, ERROR-FREE AND SAFE, BUT USE IT AT YOUR OWN RISK. TO THE FULLEST EXTENT
                PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING ALL WARRANTIES OF TITLE,
                NON-INFRINGEMENT, ACCURACY, MERCHANTABILITY, APPROPRIATENESS FOR A PARTICULAR PURPOSE, OR WARRANTIES
                WHICH MAY OCCUR IN TRANSACTIONS OR COMMERCIAL USE. WE DO NOT WARRANT THAT SOLUTIONS ARE ALWAYS SAFE OR
                ERROR FREE OR THAT THE SOLUTIONS WILL ALWAYS BE IN FUNCTION WITHOUT INTERRUPTION, DELAYS OR
                IMPERFECTIONS. WE ARE NOT RESPONSIBLE FOR THE ACTIONS OR INFORMATION OF THIRD PARTIES, AND YOU EXEMPT US
                FROM ALL CLAIMS AND DAMAGES, WHETHER KNOWN OR UNKNOWN, ARISING OUT OF OR RELATED IN ANY WAY, TO ANY
                CLAIMS YOU HAVE AGAINST THESE THIRD PARTIES. SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF IMPLIED
                WARRANTIES, SO SOME OF THE ABOVE EXCLUSIONS MAY NOT APPLY TO YOU. IF YOU ARE A PERSON RESIDING IN
                CALIFORNIA, YOU WAIVE ARTICLE 1542 OF THE CALIFORNIA CIVIL CODE WHICH STIPULATES: A general release does
                not extend to claims which the creditor does not know or suspect to exist in his or her favor at the
                time of executing the release, which if known by him or her must have materially affected his or her
                settlement with the debtor.
              </p>
              <h4 className="mt-6 mb-3">Limitation of liability</h4>
              <p>
                IN NO EVENT SHALL OUR COMPANY OR PARTNER merchants, ADVERTISERS OR SUPPLIERS BE LIABLE TO YOU OR TO ANY
                THIRD PARTY, AND THEREFORE YOU SHALL GIVE UP AND EXPRESSLY WAIVE ALL YOUR MORAL RIGHTS AND RIGHTS TO ASK
                FOR DIRECT, INDIRECT, INCIDENTAL, SPECIAL OR CONSEQUENTIAL DAMAGES IN RESULT OF ANY TYPE OTHER THAN
                REASONABLE DISBURSEMENTS, AND ALL RIGHTS TO DAMAGES AND RIGHTS, TO MULTIPLY OR INCREASE DAMAGE ARISING
                OUT OF OR IN CONNECTION WITH THE SOLUTIONS, THE CONTENT, OR ANY PRODUCT OR SERVICE PURCHASED ON THE
                SOLUTIONS, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES, AND INDEPENDENTLY ASK
                WHETHER THE CLAIM IS BASED ON CONTRACT, INJURY OR OTHER LEGAL OR EQUITABLE THEORY. WITHOUT LIMITING THE
                FOREGOING, YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT WE HAVE NO RESPONSIBILITY TO (a) BREACH ANY OTHER
                USER OF THE SOLUTIONS IN ORDER TO COMPLY WITH CODES OF CONDUCT; (b) PERSONAL INJURY OR PROPERTY DAMAGE
                OF ANY KIND, WHETHER CONTRACTUAL OR DELIBERATE, RESULTING FROM THE USE OF OUR SOLUTIONS; (c) ANY
                UNAUTHORIZED ACCESS OR USE OF OUR SECURE SERVERS AND / OR ANY PARTY AND ALL PERSONAL INFORMATION AND /
                OR FINANCIAL INFORMATION THAT IS SAVED; (d) ALL BUGS, VIRUSES, WORMS, TROJAN HORSES, DEFECTS, DELAYED
                VIRAL DEVICES OR OTHER DESTRUCTIVE ELEMENTS THAT MAY BE TRANSMITTED TO OUR SOLUTIONS OR ITS
                INTERMEDIARY; (e) ANY ERRORS, VAGUENESS, INACCURACIES OR OMISSIONS IN ANY CONTENT, OR; (f) ALL LOST,
                STOLEN OR DAMAGED PRODUCTS, OR THE FAILURE OF ANY MEDIA TO DELIVER A PRODUCT. YOUR SOLE AND EXCLUSIVE
                REMEDY FOR EXPRESSING YOUR DISCONTENT WITH SOLUTIONS IS TO STOP USING THE SOLUTIONS. THE LIMITATIONS OF
                THIS SECTION APPLY EVEN IF THE ESSENTIAL PURPOSE OF ANY REMEDY FAILED. AN ESSENTIAL ELEMENT OF THE BASIS
                OF THE MARKET CONCLUDED BETWEEN US IS BASED ON THE DISTRIBUTION OF RISK BETWEEN US. THE TOTAL AMOUNT OF
                LIABILITY ARISING FROM THESE TERMS OR FROM THE USE OF THE SOLUTIONS CAN NOT EXCEED MORE THAN ONE HUNDRED
                (100) DOLLARS OR THE AMOUNT YOU PAID US DURING THE LAST TWELVE MONTHS. SOME JURISDICTIONS DO NOT ALLOW
                THE EXCLUSION OF IMPLIED WARRANTIES, SO SOME OF THE ABOVE EXCLUSIONS MAY NOT APPLY TO YOU. IN SUCH
                CASES, OUR LIABILITY SHALL BE LIMITED TO THE EXTENT PERMITTED BY APPLICABLE LAW. IN NO EVENT SHALL
                LAWYERS’ FEES BE CHARGED OR RECOVERABLE.
              </p>
              <h4 className="mt-6 mb-3">Compensation</h4>
              <p>
                If anyone makes a claim against us related to your use of the Solutions, your User Content or a breach
                of these Terms, you agree to defend and indemnify our business and our affiliates, partner merchants,
                advertisers and suppliers, and each of our agents, distributors, directors, employees and managers,
                against all claims, damages, losses and expenses of any kind (including reasonable legal fees and
                costs). We reserve the right to take exclusive defense and control of any claim, and you agree to
                cooperate fully with us to enforce any available defense.
              </p>
              <p>Disputes, including compulsory arbitration and waiver of className action.</p>
              <p>
                Any dispute or claim related in any way to your use of the Solutions, or to products or services sold or
                distributed by or through us, will be resolved through binding arbitration rather than in court, with
                the following exceptions:
              </p>
              <ul>
                <li>You can bring claims to the Small Claims Court if your claims apply;</li>
                <li>
                  If a claim involves the Condition of license granted to you, as described in the Content Ownership and
                  Conditional Granting Licensing section above, either of us may bring a legal action in a court of
                  competent jurisdiction in the district of Québec, the district in which the parties will elect
                  domicile in accordance with the provisions of article 83 of the Civil Code of Québec. Both parties
                  hereby submit to the jurisdiction of these courts; and
                </li>
                <li>
                  in the event that the arbitration agreement of these Terms is, for any reason, deemed to be
                  unenforceable, any litigation against us (other than actions in the Small Claims Court) shall be
                  commenced in a court of competent jurisdiction in the district of Québec, district in which the
                  parties will elect domicile in accordance with the provisions of article 83 of the Civil Code of
                  Québec. Both parties hereby submit to the jurisdiction of these courts.
                </li>
              </ul>
              <p>
                The arbitration agreement of these Terms is governed by the laws of the Province of Quebec, District of
                Quebec, Canada, including its procedural provisions. The Agreement is intended to be interpreted in its
                entirety and remains in effect after the termination of these Terms. The arbitrator, and not a federal
                court or agency, or a Province or State, retains the exclusive authority, to the extent permitted by
                law, to resolve all disputes arising or relating to the interpretation, the enforceability, or formation
                of this Agreement including, but not limited to, any allegation that all or any part of this Agreement
                is invalid or may be void. There is no judge or jury in the arbitration, and the review by a court of an
                arbitral award is limited. However, the arbitrator may grant the same damages and remedies as a court
                (including an injunction and declaratory judgment or statutory damages) on an individual basis, and he
                or she must follow these Terms as a court would.
              </p>
              <p>
                To begin an arbitration proceeding, you must send a letter requesting arbitration and describing your
                application to: Sociavore Inc.,151 Charles St W #100, Kitchener, ON, N2G 1H6, Canada Attention: Legal
                Service.
              </p>
              <p>
                Each of us agrees that the arbitrator can not group more than one claim, and cannot otherwise preside
                over any form of className action, and that any dispute settlement proceeding will be conducted solely
                on an individual basis and not in the form of a className action or action on behalf of an association.
              </p>
              <p>
                You agree to waive any right to a jury trial or to participate in a className action. If this specific
                provision proves inapplicable, then the entire arbitration section will be null and void, and neither
                party will have the right to arbitrate the dispute.
              </p>
              <p>
                You agree that these Terms govern the evidence that involves trade between different states and
                provinces and that the action be construed in accordance with the laws of the Province of Quebec,
                District of Quebec, Canada, to the extent practicable.
              </p>
              <p>
                <strong>Questions</strong>
              </p>
              <p>
                If you have any questions, comments or complaints about these Terms or our Solutions, please contact us
                at:
              </p>
              <p>
                <strong>Sociavore (Sociavore Inc.)</strong>
              </p>
              <p>151 Charles St W #100, Kitchener, ON, N2G 1H6, Canada</p>
              <p>Email: hello@sociavore.co</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-accent" type="button" onClick={closeModal}>
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  )
}

export default memo(TermsModal)
